import {
    ResourceOptionalField,
    ResourceOptionalSection,
} from '@masar/common/models';
import { ChangeDetectorRef, ElementRef } from '@angular/core';
import {
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MnmFormField, MnmFormState } from '@masar/shared/components';

const requiredArrayValidator = (
    control: AbstractControl
): ValidationErrors | null => {
    const value = control.value;
    if (!value || !Array.isArray(value) || value.length === 0) {
        return { required: true };
    }
    return null;
};

const updateOptionalFields = (
    formState: MnmFormState,
    optionalFields: ResourceOptionalField[],
    fields: [string, string][]
): void => {
    fields.forEach(([optionalFieldName, controlName]) => {
        const optionalField = optionalFields.find(
            x => x.name === optionalFieldName
        );

        if (!optionalField) {
            return;
        }

        const { isEnabled, isRequired } = optionalField;
        updateFieldVisibility(formState, isEnabled, isRequired, controlName);
    });
};

const updateOptionalSections = (
    formState: MnmFormState,
    changeDetectorRef: ChangeDetectorRef,
    optionalSections: ResourceOptionalSection[],
    sections: ([string, string, string, ElementRef] | [string, string])[]
): void => {
    sections.forEach(section => {
        const [optionalSectionName, sectionName, controlName, elementRef] =
            section;

        const optionalSection = optionalSections.find(
            x => x.name === optionalSectionName
        );

        if (!optionalSection) {
            return;
        }

        updateSectionVisibility(
            formState,
            changeDetectorRef,
            optionalSection.isEnabled,
            optionalSection.isRequired,
            sectionName,
            controlName,
            elementRef
        );
    });
};

const updateFieldVisibility = (
    formState: MnmFormState,
    isEnabled: boolean,
    isRequired: boolean,
    fieldName: string
): void => {
    const field = formState.get(fieldName) as MnmFormField & {
        baseValidators?: ValidatorFn[];
    };

    // Cache the original validators once, so toggling optional settings
    // never erases validators like emailValidator.
    if (field && !field.baseValidators) {
        field.baseValidators = [...(field.validators || [])];
    }

    const baseValidators = (field?.baseValidators || []).filter(
        validator => validator !== Validators.required
    );

    formState.setValidators(
        fieldName,
        isEnabled
            ? isRequired
                ? [Validators.required, ...baseValidators]
                : baseValidators
            : []
    );

    if (isEnabled) formState.showField(fieldName);
    else formState.hideField(fieldName);
};

const updateSectionFieldsVisibility = (
    formState: MnmFormState,
    section: MnmFormField,
    isVisible: boolean
): void => {
    if (!section?.fields) return;

    section.fields.forEach((field: MnmFormField) => {
        if (field.name) {
            isVisible
                ? formState.showField(field.name)
                : formState.hideField(field.name);
        }
        if (field.fields) {
            updateSectionFieldsVisibility(formState, field, isVisible);
        }
    });
};

const updateSectionVisibility = (
    formState: MnmFormState,
    changeDetectorRef: ChangeDetectorRef,
    isEnabled: boolean,
    isRequired: boolean,
    sectionName: string,
    controlName?: string,
    elementRef?: ElementRef
): void => {
    const section = formState.getSection(sectionName);

    if (!isEnabled) {
        formState.hideField(sectionName);
        if (section) {
            section.isSectionRequired = false;
            updateSectionFieldsVisibility(formState, section, false);
        }
        if (controlName) {
            formState.setValidators(controlName, []);
        }
        return;
    }

    formState.showField(sectionName);

    if (section) {
        section.isSectionRequired = isRequired;
        updateSectionFieldsVisibility(formState, section, true);
    }

    if (controlName) {
        formState.setValidators(
            controlName,
            isRequired ? [requiredArrayValidator] : []
        );

        const control = formState.group.controls[controlName];
        if (
            control &&
            (control.value === null || control.value === undefined)
        ) {
            control.setValue(isRequired ? [{}] : []);
            control.updateValueAndValidity();
        }
    }

    if (controlName && elementRef) {
        setTimeout(() => {
            const field = formState.get(controlName);
            if (field) {
                field.customField = elementRef;
                // Trigger validation after list-field component processes the value
                const control = formState.group.controls[controlName];
                if (control && isRequired) {
                    control.updateValueAndValidity();
                }
                changeDetectorRef.detectChanges();
            }
        }, 10);
    }
};

export const optionalFieldAndSectionHandler = (
    formState: MnmFormState,
    changeDetectorRef: ChangeDetectorRef,
    fields?: {
        optionalFields: ResourceOptionalField[];
        fields: [string, string][];
    },
    sections?: {
        optionalSections: ResourceOptionalSection[];
        sections: ([string, string, string, ElementRef] | [string, string])[];
    }
): void => {
    if (fields)
        updateOptionalFields(formState, fields.optionalFields, fields.fields);

    if (sections)
        updateOptionalSections(
            formState,
            changeDetectorRef,
            sections.optionalSections,
            sections.sections
        );
};
